import { Button } from 'antd';
import bytes from 'bytes';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import appIcon from '../../static/comt_app_default.svg';
import specialIcon from '../../static/comt_special.svg';
import useLoginStatus, { handleLogin } from '../../utils/hooks/useLoginStatus';
import Toast from '../Toast/Toast';
import { logging } from './../../actions/log';
import {
  COMT_IMG_LIMIT,
  COMT_MAX_TEXT_COUNT,
  COMT_SOFT_LIMIT,
  COMT_SPECIAL_LIMIT,
} from './../Comment/config';
import styles from './Editor.module.less';
import { EMOJI_LIST } from './emoji';
import SelectApp from './SelectApp';
import SelectZt from './SelectZt';
import UploadOSS from './UploadOSS';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

function ComtEditor({
  onSubmit,
  commentContent,
  mentionUsers,
  classWrapper = '',
  placeHolder,
  hasScore,
  requestInfo,
  score,
  commentInfo,
  commentTargetInfo,
  replyInfo,
}) {
  const logInfoData = useSelector(selectLogInfo);
  const dispatch = useDispatch();
  const loginStatus = useLoginStatus();
  const [defaultVisible, setDefaultVisible] = useState(true);
  // 选框visible
  const [appSelectVisible, setAppSelectVisible] = useState(false);
  const [ztSelectVisible, setZtSelectVisible] = useState(false);
  const [userSelectVisible, setUserSelectVisible] = useState(false);
  const [emojiVisible, setEmojiVisible] = useState(false);
  // 表单内容
  const [txtCount, setTxtCount] = useState(0);
  const [softInfos, setSoftInfos] = useState([]);
  const [specialInfos, setSpecialInfos] = useState([]);
  const [imgs, setImgs] = useState([]);
  const logPre = requestInfo?.type === '3' ? 'Forum_' : '';

  const TextChange = useCallback(() => {
    let doc = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
    let len = doc.innerText.length;
    setTxtCount(len);
    setDefaultVisible(!len);
  }, [commentInfo]);

  useEffect(() => {
    if (commentContent) {
      let { appList, specialList, imgs, text } = commentContent;
      if (appList) {
        setSoftInfos(appList);
      }
      if (specialList) {
        setSpecialInfos(specialList);
      }
      if (imgs) {
        setImgs(imgs);
      }
      let doc = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
      doc.innerHTML = text || '';
      setTxtCount(text ? doc.innerText.length : 0);
      setDefaultVisible(!text);
      if (!text) {
        doc.focus();
      }
    } else {
      let doc = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
      doc.focus();
    }
  }, [commentContent]);

  const closeSelectPop = () => {
    setZtSelectVisible(false);
    setAppSelectVisible(false);
    setUserSelectVisible(false);
    setEmojiVisible(false);
  };

  useEffect(() => {
    let doc = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
    doc.addEventListener('input', e => {
      if (doc.innerHTML.indexOf('<img') > -1) {
        document.execCommand('undo');
      }
      TextChange();
    });
    document.addEventListener('click', () => {
      closeSelectPop();
    });
    return () => {
      document.removeEventListener('click', () => {});
    };
  }, []);

  const softItem = (soft, idx) => {
    return (
      <div className="tag-item" key={idx}>
        <img alt="" src={soft.logoFile || appIcon} className="tag-icon" />
        <span className="soft-name">{soft.name}</span>
        <i
          className="iconfont icon-close"
          onClick={() => {
            softInfos.splice(idx, 1);
            setSoftInfos([...softInfos]);
          }}
        />
      </div>
    );
  };

  const specialItem = (special, idx) => {
    return (
      <div className="tag-item " key={idx}>
        <img alt="" src={specialIcon} className="tag-icon" />
        <span
          className="special-name"
          dangerouslySetInnerHTML={{
            __html: special.name,
          }}
        />
        <i
          className="iconfont icon-close"
          onClick={() => {
            specialInfos.splice(idx, 1);
            setSpecialInfos([...specialInfos]);
          }}
        />
      </div>
    );
  };

  const handleInsert = el => {
    let flag = document.execCommand('insertHTML', null, el);
    if (!flag) {
      let doc = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
      let text = doc.innerHTML + el;
      doc.innerHTML = text;
    }
  };

  const addAtUser = useCallback(
    data => {
      handleInsert(
        `<span contentEditable="false" data-user="${
          data.userId
        }" key="${Date.now()}" class="atUser">@${data.nickName}</span>&nbsp;`,
      );
      TextChange();
    },
    [commentInfo],
  );

  const addEmoji = useCallback(
    el => {
      handleInsert(el);
      setEmojiVisible(false);
      TextChange();
    },
    [commentInfo],
  );

  const clickSubmit = useCallback(() => {
    if (txtCount > COMT_MAX_TEXT_COUNT) {
      Toast.error({
        content: '糟糕，输入内容超过最大限制',
      });
      return false;
    }
    if (!commentInfo) {
      if ((specialInfos.length > 0 || imgs.length > 0 || softInfos.length > 0) && !txtCount) {
        Toast.error({
          content: '请输入评论文案内容',
        });
        return false;
      }
    }
    if (onSubmit) {
      const txtDom = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
      let arr = txtDom.getElementsByClassName('atUser');
      let mentions = [];
      if (arr.length > 0) {
        for (let i = 0; i < arr.length; i++) {
          mentions.push(arr[i].dataset.user);
        }
      }
      let obj = {
        text: txtDom.innerHTML,
        specialInfos,
        softInfos: softInfos.map(el => {
          return { softId: el.softId, name: el.name };
        }),
        imgs,
        mentionUserIds: mentions,
      };
      if (replyInfo) {
        obj.text = `@${replyInfo.nickName || '商店用户'}:${txtDom.innerHTML}`;
      } else if (commentInfo) {
        obj.text = `@${commentInfo.nickName || '商店用户'}:${txtDom.innerHTML}`;
      }
      onSubmit(obj);
    }
  }, [specialInfos, softInfos, imgs, txtCount, commentInfo, replyInfo, score]);

  return (
    <div className={styles.ComtEditor}>
      {defaultVisible && (
        <div className={classNames('default-con', classWrapper)}>{placeHolder}</div>
      )}
      <div
        className={classNames('editor-box', classWrapper)}
        onClick={() => {
          if (txtCount === 0) {
            const doc = document.getElementById(`${commentInfo?.id || ''}ComtEditor`);
            doc.focus();
          }
        }}
      >
        <blockquote
          className="editor-con"
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          id={`${commentInfo?.id || ''}ComtEditor`}
        />
        <div
          className="img-area"
          id={`${commentInfo?.id || ''}EditorUpload`}
          style={{
            visibility: imgs.length === 0 ? 'hidden' : 'visible',
            height: imgs.length === 0 && 0,
          }}
        >
          <UploadOSS
            limit={COMT_IMG_LIMIT}
            accept=".gif,.jpg,.png"
            value={imgs}
            sizeLimit={bytes('5MB')}
            onChange={list => {
              setImgs([...list]);
            }}
          />
        </div>
        {((softInfos && softInfos.length > 0) || (specialInfos && specialInfos.length > 0)) && (
          <div className="tag-area">
            {softInfos.length > 0 &&
              softInfos.map((el, idx) => {
                return softItem(el, idx);
              })}
            {specialInfos.length > 0 &&
              specialInfos.map((el, idx) => {
                return specialItem(el, idx);
              })}
          </div>
        )}
      </div>
      <div
        className={classNames('text-count', classWrapper, txtCount > COMT_MAX_TEXT_COUNT && 'red')}
      >
        {txtCount}/{COMT_MAX_TEXT_COUNT}
      </div>
      <div className={classNames('opt-group')}>
        <div
          className="opt-item"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <i
            className="iconfont icon-comt_emoji"
            title="插入表情"
            onClick={e => {
              e.stopPropagation();
              closeSelectPop();
              setEmojiVisible(true);
            }}
          />
          {emojiVisible && (
            <div className="emoji-list">
              {EMOJI_LIST.map((el, idx) => {
                return (
                  <span
                    key={idx}
                    className="emoji-item"
                    onClick={e => {
                      e.stopPropagation();
                      addEmoji(el);
                    }}
                  >
                    {el}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div
          className="opt-item"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <i
            className="iconfont icon-comt_img"
            title="插入图片"
            onClick={e => {
              e.stopPropagation();
              if (imgs.length < COMT_IMG_LIMIT) {
                closeSelectPop();
                let dom = document.getElementById(`${commentInfo?.id || ''}EditorUpload`);
                let btn = dom.getElementsByClassName('ant-upload')[1];
                btn.click();
              } else {
                Toast.error({ content: `最多插入${COMT_IMG_LIMIT}个图片` });
              }
            }}
          />
        </div>
        <div
          className="opt-item"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <i
            className="iconfont icon-comt_app"
            title="插入应用"
            onClick={e => {
              e.stopPropagation();
              if (softInfos.length < COMT_SOFT_LIMIT) {
                closeSelectPop();
                setAppSelectVisible(true);
              } else {
                Toast.error({ content: `最多插入${COMT_SOFT_LIMIT}个应用` });
              }
            }}
          />
          {appSelectVisible && (
            <SelectApp
              onSelect={app => {
                let arr = softInfos.filter(el => el.softId === app.softId);
                if (!arr.length) {
                  setSoftInfos([...softInfos, app]);
                } else {
                  Toast.error({
                    content: '您已经选择过该应用',
                  });
                }
                setAppSelectVisible(false);
              }}
            />
          )}
        </div>
        <div
          className="opt-item"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <i
            title="插入专题"
            className="iconfont icon-comt_zt"
            onClick={e => {
              e.stopPropagation();
              if (specialInfos.length < COMT_SPECIAL_LIMIT) {
                closeSelectPop();
                setZtSelectVisible(true);
              } else {
                Toast.error({ content: `最多插入${COMT_SPECIAL_LIMIT}个专题` });
              }
            }}
          />
          {ztSelectVisible && (
            <SelectZt
              onSelect={zt => {
                let arr = specialInfos.filter(el => el.ztId === zt.ztId);
                if (!arr.length) {
                  setSpecialInfos([...specialInfos, zt]);
                } else {
                  Toast.error({
                    content: '您已经选择过该专题',
                  });
                }
                setZtSelectVisible(false);
              }}
            />
          )}
        </div>
        {/* <div className="opt-item">
          <i
            className="iconfont icon-comt_at"
            title="@用户"
            onClick={e => {
              e.stopPropagation();
              closeSelectPop();
              setUserSelectVisible(true);
            }}
          />
          {userSelectVisible && mentionUsers.length > 0 && (
            <ul className="user-list">
              {mentionUsers.map(el => {
                return (
                  <li
                    key={el.userId}
                    className="user-item"
                    onClick={() => {
                      addAtUser(el);
                      setUserSelectVisible(false);
                    }}
                  >
                    {el.nickName}
                  </li>
                );
              })}
            </ul>
          )}
        </div> */}
      </div>
      <div className="btn-group">
        {!classWrapper && (
          <div
            className="cancel-btn"
            onClick={() => {
              onSubmit && onSubmit(null);
            }}
          >
            取消
          </div>
        )}
        <Button
          type="primary"
          className="submit-btn"
          disabled={!hasScore && !txtCount}
          onClick={() => {
            if (commentInfo) {
              dispatch(
                logging({
                  category: 'C',
                  action: logPre + 'Detail_Review_Submit',
                  param2: {
                    version: commentTargetInfo?.version,
                    reviewId: replyInfo?.id,
                    commentId: commentInfo?.id,
                    id: requestInfo?.bizIdentity?.replace('zt_', '')?.replace('tz_', ''),
                    source: logInfoData.source,
                    source_p: logInfoData.source_p,
                    source_pp: logInfoData.source_pp,
                  },
                }),
              );
            } else {
              dispatch(
                logging({
                  category: 'C',
                  action: logPre + 'Detail_Comment_Submit',
                  param2: {
                    grade: score || '',
                    id: requestInfo?.bizIdentity?.replace('zt_', '')?.replace('tz_', ''),
                    source: logInfoData.source,
                    source_p: logInfoData.source_p,
                    source_pp: logInfoData.source_pp,
                  },
                }),
              );
            }

            if (!loginStatus) {
              handleLogin();
            } else {
              clickSubmit();
            }
          }}
        >
          发布
        </Button>
      </div>
    </div>
  );
}

export default memo(ComtEditor);
